<template>
  <article
    class="assembly-node-card"
    :class="toneClass"
    @mouseenter="handleCardMouseEnter"
    @mouseleave="handleCardMouseLeave"
  >
    <span class="assembly-node-card__type-bar" aria-hidden="true" />

    <div class="assembly-node-card__main">
      <header class="assembly-node-card__header">
        <div class="assembly-node-card__identity">
          <h3 class="assembly-node-card__name">{{ node.name }}</h3>
          <span class="assembly-node-card__kind">{{ nodeTypeText }}</span>
        </div>

        <div class="assembly-node-card__side">
          <div
            v-if="!readonlyPreview"
            class="assembly-node-card__actions"
          >
            <n-tooltip
              trigger="hover"
              placement="top"
              :show-arrow="false"
              :theme-overrides="tooltipThemeOverrides"
              :content-style="tooltipContentStyle"
            >
              <template #trigger>
                <n-button
                  class="assembly-node-card__action-button"
                  size="tiny"
                  circle
                  quaternary
                  aria-label="切换采纳版本"
                  data-switch-node-version
                  data-testid="data-switch-node-version"
                  @click="emit('switchVersion', node)"
                >
                  <template #icon>
                    <n-icon>
                      <ArrowsHorizontal />
                    </n-icon>
                  </template>
                </n-button>
              </template>
              <span>切换采纳版本</span>
            </n-tooltip>
          </div>
        </div>
      </header>

      <section class="assembly-node-card__version">
        <span class="assembly-node-card__label">采纳版本</span>
        <n-tooltip
          trigger="hover"
          placement="top-start"
          :show-arrow="false"
          :theme-overrides="tooltipThemeOverrides"
          :content-style="tooltipContentStyle"
        >
          <template #trigger>
            <span class="assembly-node-card__version-text">
              {{ node.adoptedVersionLabel }}
            </span>
          </template>
          <span>{{ node.adoptedVersionLabel }}</span>
        </n-tooltip>
      </section>

      <section class="assembly-node-card__branches">
        <div class="assembly-node-card__branch-heading">
          <span>负责分支</span>
          <n-tag
            class="assembly-node-card__branch-count"
            size="small"
            round
            :bordered="false"
          >
            {{ node.branchCount }} 分支
          </n-tag>
        </div>
        <div
          class="assembly-node-card__branch-list"
          :class="{ 'assembly-node-card__branch-list--scrollable': node.branches.length > 3 }"
        >
          <div
            v-if="node.branches.length === 0"
            class="assembly-node-card__branch-empty"
          >
            暂无负责分支
          </div>
          <div
            v-for="branch in node.branches"
            :key="`${branch.repositoryId}:${branch.branchId}`"
            class="assembly-node-card__branch-row"
          >
            <span class="assembly-node-card__branch-dot" aria-hidden="true" />
            <span class="assembly-node-card__branch-name">{{ branch.branchName }}</span>
            <n-tooltip
              v-if="branch.canEdit && !readonlyPreview"
              trigger="hover"
              placement="top"
              :show-arrow="false"
              :theme-overrides="tooltipThemeOverrides"
              :content-style="tooltipContentStyle"
            >
              <template #trigger>
                <n-button
                  class="assembly-node-card__branch-edit-button"
                  size="tiny"
                  circle
                  quaternary
                  aria-label="进入负责分支工作区"
                  data-edit-responsible-branch
                  :data-branch-id="branch.branchId"
                  @click.stop="emit('editResponsibleBranch', branch)"
                >
                  <template #icon>
                    <n-icon>
                      <Edit />
                    </n-icon>
                  </template>
                </n-button>
              </template>
              <span>进入工作区</span>
            </n-tooltip>
            <span class="assembly-node-card__assignee">{{ branch.assigneeName }}</span>
          </div>
        </div>
      </section>
    </div>
  </article>
</template>

<script setup lang="ts">
/**
 * 装配体子节点卡片。
 *
 * 该组件只呈现单个子节点摘要并把动作交给父级处理，避免 UI 卡片直接接触列表加载或版本切换流程。
 */
import { computed } from 'vue';
import { NButton, NIcon, NTag, NTooltip } from 'naive-ui';
import { ArrowsHorizontal, Edit } from '@vicons/carbon';
import { useEditorTooltipTheme } from '@/hooks/useEditorTooltipTheme';
import type {
  AssemblyChildNodeBranchSummary,
  AssemblyChildNodeSummary,
} from '@/services/assembly-branch-node-list';

const props = defineProps<{
  /** 当前卡片展示的装配体直接子节点。 */
  node: AssemblyChildNodeSummary;
  /** 只读预览态只允许查看，不展示编辑动作。 */
  readonlyPreview: boolean;
}>();

const emit = defineEmits<{
  /** 请求切换当前节点被父装配体采纳的提交版本。 */
  switchVersion: [node: AssemblyChildNodeSummary];
  /** 请求进入当前节点的可编辑负责分支工作区。 */
  editResponsibleBranch: [branch: AssemblyChildNodeBranchSummary];
  /** 请求同步当前节点的动态高亮状态。 */
  hoverChange: [hovered: boolean];
}>();

const { tooltipThemeOverrides, tooltipContentStyle } = useEditorTooltipTheme({
  maxWidth: '280px',
});

/**
 * 将服务层节点类型翻译为 SolidWorks 风格的结构说明。
 *
 * @returns 可直接展示在卡片中的节点类型文案。
 */
const nodeTypeText = computed(() => (props.node.nodeType === 'assembly' ? '装配体节点' : '零件节点'));

/**
 * 按节点类型生成视觉 tone 类名。
 *
 * @returns 用于区分零件与子装配体的 CSS 类名。
 */
const toneClass = computed(() => (
  props.node.nodeType === 'assembly'
    ? 'assembly-node-card--assembly'
    : 'assembly-node-card--part'
));

/**
 * 处理卡片鼠标移入。
 *
 * 说明：卡片只负责发出 UI hover 信号，不直接访问 Viewer 或场景对象，
 * 具体的模型动态高亮由 Editor 统一接入 cad-core。
 */
function handleCardMouseEnter(): void {
  emit('hoverChange', true);
}

/**
 * 处理卡片鼠标移出。
 *
 * 说明：鼠标移出必须同步清理动态高亮，避免 SolidWorks 预选择反馈残留。
 */
function handleCardMouseLeave(): void {
  emit('hoverChange', false);
}
</script>

<style scoped>
.assembly-node-card {
  --assembly-node-card-tone: var(--n-primary-color, var(--puppy-primary));
  --assembly-node-card-text: var(--assembly-node-list-text, var(--n-text-color, #e5e7eb));
  --assembly-node-card-text-2: var(--assembly-node-list-text-2, var(--n-text-color-2, #a8adb6));
  --assembly-node-card-text-3: var(--assembly-node-list-text-3, var(--n-text-color-3, #7f8794));
  --assembly-node-card-version-color: color-mix(in srgb, var(--assembly-node-card-tone) 52%, var(--assembly-node-card-text) 48%);
  --assembly-node-card-version-bg: var(
    --assembly-node-list-version-bg,
    color-mix(in srgb, var(--assembly-node-card-tone) 12%, transparent)
  );
  --assembly-node-card-version-border: var(
    --assembly-node-list-version-border,
    color-mix(in srgb, var(--assembly-node-card-tone) 24%, transparent)
  );
  --assembly-node-card-branch-color: var(--assembly-node-card-text-2);
  --assembly-node-card-bg: var(
    --assembly-node-list-card-bg,
    color-mix(in srgb, var(--n-card-color, #17171c) 92%, var(--assembly-node-card-text) 8%)
  );
  --assembly-node-card-hover-bg: var(
    --assembly-node-list-card-hover-bg,
    color-mix(in srgb, var(--n-card-color, #17171c) 88%, var(--assembly-node-card-text) 12%)
  );
  --assembly-node-card-soft-border: var(
    --assembly-node-list-card-border,
    color-mix(in srgb, var(--assembly-node-card-text) 18%, transparent)
  );
  --assembly-node-card-hover-border: var(
    --assembly-node-list-card-hover-border,
    color-mix(in srgb, var(--assembly-node-card-tone) 36%, var(--assembly-node-card-soft-border))
  );
  --assembly-node-card-branch-hover-bg: var(
    --assembly-node-list-branch-hover-bg,
    color-mix(in srgb, var(--assembly-node-card-tone) 8%, transparent)
  );
  box-sizing: border-box;
  display: flex;
  gap: 9px;
  padding: 9px 10px;
  border: 1px solid var(--assembly-node-card-soft-border);
  border-radius: 8px;
  background: var(--assembly-node-card-bg);
  color: var(--assembly-node-card-text);
  box-shadow: var(--assembly-node-list-card-shadow, inset 0 1px 0 color-mix(in srgb, var(--assembly-node-card-text) 4%, transparent));
  transition:
    border-color 0.16s ease,
    background 0.16s ease;
}

.assembly-node-card:hover {
  border-color: var(--assembly-node-card-hover-border);
  background: var(--assembly-node-card-hover-bg);
  box-shadow:
    var(--assembly-node-list-card-shadow, inset 0 1px 0 color-mix(in srgb, var(--assembly-node-card-text) 4%, transparent)),
    0 0 0 1px color-mix(in srgb, var(--assembly-node-card-tone) 10%, transparent);
}

.assembly-node-card--part {
  --assembly-node-card-tone: var(--n-success-color, #18a058);
  --assembly-node-card-version-bg: var(--assembly-node-list-part-version-bg, var(--assembly-node-list-version-bg));
  --assembly-node-card-version-border: var(--assembly-node-list-part-version-border, var(--assembly-node-list-version-border));
}

.assembly-node-card--assembly {
  --assembly-node-card-tone: var(--n-info-color, var(--n-primary-color, var(--puppy-primary)));
  --assembly-node-card-version-bg: var(--assembly-node-list-assembly-version-bg, var(--assembly-node-list-version-bg));
  --assembly-node-card-version-border: var(--assembly-node-list-assembly-version-border, var(--assembly-node-list-version-border));
}

.assembly-node-card__type-bar {
  flex: 0 0 3px;
  align-self: stretch;
  border-radius: 999px;
  background: var(--assembly-node-card-tone);
  opacity: 0.82;
}

.assembly-node-card__main {
  display: flex;
  flex: 1 1 auto;
  min-width: 0;
  flex-direction: column;
  gap: 7px;
}

.assembly-node-card__header {
  display: flex;
  min-width: 0;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
}

.assembly-node-card__identity {
  min-width: 0;
}

.assembly-node-card__name {
  min-width: 0;
  margin: 0;
  overflow: hidden;
  color: var(--assembly-node-card-text);
  font-size: 13px;
  font-weight: 700;
  line-height: 1.3;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.assembly-node-card__kind,
.assembly-node-card__label,
.assembly-node-card__branch-heading {
  color: var(--assembly-node-card-text-3);
  font-size: 11px;
  line-height: 1.2;
}

.assembly-node-card__kind {
  display: block;
  margin-top: 3px;
  color: var(--assembly-node-card-text-3);
  font-weight: 600;
}

.assembly-node-card__side {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 6px;
}

.assembly-node-card__branch-count {
  background: color-mix(in srgb, var(--assembly-node-card-text) 12%, transparent);
  color: color-mix(in srgb, var(--assembly-node-card-text) 76%, var(--assembly-node-card-tone) 24%);
  font-weight: 600;
}

.assembly-node-card__version {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 4px;
  align-items: stretch;
  padding: 6px 8px;
  border: 1px solid var(--assembly-node-card-version-border);
  border-radius: 7px;
  background: var(--assembly-node-card-version-bg);
  box-shadow: inset 0 1px 0 color-mix(in srgb, var(--assembly-node-card-text) 5%, transparent);
}

.assembly-node-card__branches {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 5px;
  padding-top: 6px;
  border-top: 1px solid color-mix(in srgb, var(--assembly-node-card-text) 8%, transparent);
}

.assembly-node-card__version-text {
  display: block;
  min-width: 0;
  overflow: hidden;
  color: var(--assembly-node-card-version-color);
  font-size: 12px;
  font-weight: 700;
  line-height: 1.35;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.assembly-node-card__branch-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.assembly-node-card__branch-heading :deep(.n-tag) {
  background: color-mix(in srgb, var(--assembly-node-card-text) 8%, transparent);
  color: var(--assembly-node-card-text-2);
}

.assembly-node-card__branch-list {
  display: flex;
  min-width: 0;
  max-height: 86px;
  flex-direction: column;
  gap: 2px;
  overflow-y: auto;
  padding-right: 2px;
}

.assembly-node-card__branch-list--scrollable {
  padding-right: 5px;
}

.assembly-node-card__branch-list::-webkit-scrollbar {
  width: 4px;
}

.assembly-node-card__branch-list::-webkit-scrollbar-thumb {
  border-radius: 999px;
  background: color-mix(in srgb, var(--assembly-node-card-text) 18%, transparent);
}

.assembly-node-card__branch-list::-webkit-scrollbar-track {
  background: transparent;
}

.assembly-node-card__branch-row {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto auto;
  gap: 6px;
  align-items: center;
  min-width: 0;
  padding: 3px 2px;
  border-radius: 5px;
  background: transparent;
  font-size: 12px;
  line-height: 1.25;
  transition: background 0.16s ease;
}

.assembly-node-card__branch-row:hover {
  background: var(--assembly-node-card-branch-hover-bg);
}

.assembly-node-card__branch-row:hover .assembly-node-card__branch-name {
  color: var(--assembly-node-card-text);
}

.assembly-node-card__branch-dot {
  width: 4px;
  height: 4px;
  border-radius: 999px;
  background: var(--assembly-node-card-text-3);
}

.assembly-node-card__branch-empty {
  color: var(--assembly-node-card-text-3);
  font-size: 12px;
}

.assembly-node-card__branch-name {
  min-width: 0;
  overflow: hidden;
  color: var(--assembly-node-card-branch-color);
  font-weight: 500;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.assembly-node-card__assignee {
  color: var(--assembly-node-card-text-3);
  white-space: nowrap;
}

.assembly-node-card__actions {
  display: flex;
  flex: 0 0 auto;
  gap: 1px;
  align-items: center;
  justify-content: flex-start;
}

.assembly-node-card__action-button {
  color: var(--n-primary-color);
}

.assembly-node-card__action-button :deep(.n-button__icon) {
  font-size: 14px;
}

.assembly-node-card__action-button:hover {
  color: var(--n-primary-color-hover, var(--n-primary-color));
}

.assembly-node-card__branch-edit-button {
  width: 18px;
  height: 18px;
  min-width: 18px;
  color: var(--n-primary-color);
}

.assembly-node-card__branch-edit-button :deep(.n-button__icon) {
  font-size: 12px;
}

.assembly-node-card__branch-edit-button:hover {
  color: var(--n-primary-color-hover, var(--n-primary-color));
}
</style>
